import { Box, Typography, Paper } from "@mui/material";
import {
  PieChart, Pie, Cell,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

// Extract year from YYYY-MM-DD
const getYear = (dateStr) => {
  if (!dateStr) return null;
  const y = parseInt(dateStr.substring(0, 4));
  return isNaN(y) ? null : y;
};

export default function Overview({ companies }) {

  const totalCompanies = companies.length;
  const totalLeads = companies.filter(c => c["Lead Type"] === "ACCEPT").length;

  // Classification Summary
  const classificationData = [
    { name: "Leads", value: totalLeads },
    { name: "Discard", value: companies.filter(c => c["Lead Type"] === "NOT ACCEPT").length },
    { name: "Unsure", value: companies.filter(c => c["Lead Type"] === "FUZZY").length },
  ];
  const CLASS_COLORS = ["#4caf50", "#f44336", "#fb8c00"];

  // Bucket Industry Distribution
  const industryMap = {};
  companies.forEach((c) => {
    const buckets = c.BucketIndustries || [];
    buckets.forEach((b) => {
      industryMap[b] = (industryMap[b] || 0) + 1;
    });
  });

  const industryData = Object.entries(industryMap).map(
    ([industry, count]) => ({ industry, count })
  );

  // Funding Stage Distribution
  const fundingStageMap = {};
  companies.forEach((c) => {
    const stage = c["Investment Stage"] || "Unknown";
    fundingStageMap[stage] = (fundingStageMap[stage] || 0) + 1;
  });

  const fundingStageData = Object.entries(fundingStageMap).map(
    ([stage, count]) => ({ stage, count })
  );

  // Geography Distribution
  const countryMap = {};
  companies.forEach((c) => {
    const country = c["Headquarters Location"] || "Unknown";
    countryMap[country] = (countryMap[country] || 0) + 1;
  });

  const countryData = Object.entries(countryMap).map(
    ([country, count]) => ({ country, count })
  );

  // Company Age Distribution
  const currentYear = new Date().getFullYear();
  const ages = companies
    .map((c) => {
      const year = getYear(c.Founded);
      return year ? currentYear - year : null;
    })
    .filter((a) => a !== null);

  const ageBins = [
    { label: "0–3 yrs", min: 0, max: 3 },
    { label: "3–6 yrs", min: 3, max: 6 },
    { label: "6–10 yrs", min: 6, max: 10 },
    { label: "10+ yrs", min: 10, max: 500 },
  ];

  const ageData = ageBins.map((bin) => ({
    year: bin.label,
    count: ages.filter((a) => a >= bin.min && a < bin.max).length,
  }));

  return (
    <Box sx={{ mt: 3 }}>

      {/* Summary Box */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Summary
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          <strong>Total Companies:</strong> {totalCompanies}
        </Typography>
        <Typography variant="body1">
          <strong>Leads:</strong> {totalLeads}
        </Typography>
      </Paper>

      {/* Classification Summary */}
      <Typography variant="h5" sx={{ mb: 2 }}>
        Classification Summary
      </Typography>

      <ResponsiveContainer width="100%" height={400}>
        <PieChart>
          <Pie
            data={classificationData}
            dataKey="value"
            nameKey="name"
            outerRadius={130}
            label
          >
            {classificationData.map((entry, i) => (
              <Cell key={i} fill={CLASS_COLORS[i]} />
            ))}
          </Pie>
          <Tooltip />

          {/* UPDATED: Move legend down so it doesn’t clash with labels */}
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{ paddingTop: 20, marginTop: 5 }}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Industry Distribution */}
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Industry Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={industryData}>
          <CartesianGrid strokeDasharray="3 3" />

          {/* UPDATED: Horizontal labels */}
          <XAxis
            dataKey="industry"
            angle={0}
            textAnchor="middle"
            height={50}
          />

          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#1976d2" />
        </BarChart>
      </ResponsiveContainer>

      {/* Funding Stage */}
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Funding Stage Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={fundingStageData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="stage" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#9c27b0" />
        </BarChart>
      </ResponsiveContainer>

      {/* Geography */}
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Geographic Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={countryData}>
          <CartesianGrid strokeDasharray="3 3" />

          {/* UPDATED: Vertical labels with larger height to prevent clipping */}
          <XAxis
            dataKey="country"
            angle={-90}
            textAnchor="end"
            height={120}
          />

          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#4caf50" />
        </BarChart>
      </ResponsiveContainer>

      {/* Age Distribution */}
      <Typography variant="h5" sx={{ mt: 5, mb: 2 }}>
        Company Age Distribution
      </Typography>

      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={ageData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="count" fill="#3f51b5" />
        </BarChart>
      </ResponsiveContainer>

    </Box>
  );
}
